const serialize = (value) => JSON.stringify(value ?? null);
const timeKey = (value) => String(value ?? "").slice(0, 5);
const dateKey = (entry) =>
  [entry.date, timeKey(entry.startTime), timeKey(entry.finishTime)].join("|");

const mergeUnchangedFields = (current, submitted, saved, excluded = []) => {
  Object.entries(saved).forEach(([field, value]) => {
    if (excluded.includes(field)) return;
    if (serialize(current[field]) === serialize(submitted[field])) {
      current[field] = value;
    }
  });
};

// Keep the original row objects so typing, additions and removals during a save
// survive the response, while new rows receive their persisted IDs.
export const reconcileSavedResourceSchedule = (
  current,
  submitted,
  rows,
  saved,
) => {
  const remainingDates = [...saved.dates];
  submitted.dates.forEach((date, index) => {
    const row = rows[index];
    const savedIndex = remainingDates.findIndex((candidate) =>
      date.id || date.date_id
        ? Number(candidate.id ?? candidate.date_id) ===
          Number(date.id ?? date.date_id)
        : dateKey(candidate) === dateKey(date),
    );
    if (savedIndex < 0)
      throw new Error(
        "Unable to reconcile the saved schedule. Reload the latest schedule before saving again.",
      );
    const savedDate = remainingDates.splice(savedIndex, 1)[0];
    if (!current.dates.includes(row.entry)) return;
    const addressChanged =
      serialize(row.entry.location_address) !==
        serialize(date.location_address) ||
      serialize(row.entry.address_id) !== serialize(date.address_id);
    mergeUnchangedFields(row.entry, date, savedDate, [
      "resources",
      "id",
      "date_id",
      ...(addressChanged
        ? [
            "address",
            "address_id",
            "location_address",
            "base_distance_miles",
            "base_duration_minutes",
          ]
        : []),
    ]);
    row.entry.id = savedDate.id;
    row.entry.date_id = savedDate.date_id ?? savedDate.id;

    const remainingResources = [...(savedDate.resources ?? [])];
    (date.resources ?? []).forEach((resource, resourceIndex) => {
      const liveResource = row.resources[resourceIndex];
      const savedResourceIndex = remainingResources.findIndex((candidate) =>
        resource.id
          ? Number(candidate.id) === Number(resource.id)
          : Number(candidate.product_id ?? candidate.product?.id) ===
            Number(resource.product_id ?? resource.product?.id),
      );
      if (savedResourceIndex < 0)
        throw new Error(
          "Unable to reconcile the saved resources. Reload the latest schedule before saving again.",
        );
      const savedResource = remainingResources.splice(savedResourceIndex, 1)[0];
      if (!row.entry.resources.includes(liveResource)) return;
      const productChanged =
        Number(liveResource.product_id) !== Number(resource.product_id);
      mergeUnchangedFields(liveResource, resource, savedResource, [
        "id",
        ...(productChanged ? ["product"] : []),
      ]);
      liveResource.id = savedResource.id;
    });
  });
  current.resource_revision = saved.resource_revision;
};
